import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const SITE_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://giftopay.com";

export default function BrandsHub() {
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    brands.forEach((b) => {
      g[b.category] = g[b.category] || [];
      g[b.category].push(b);
    });
    return g;
  }, [brands]);

  return (
    <div className="min-h-screen bg-white py-16" data-testid="brands-hub">
      <Seo
        title="Sell Gift Cards — All Supported Brands"
        description="Browse all 45+ gift card brands accepted on Giftopay. Sell Amazon, Apple, Walmart, Steam, Visa, Mastercard and more across the US, UK, UAE & Canada."
        canonical={`${SITE_ORIGIN}/brands`}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">All brands</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0B3C5D] mt-3 tracking-tight">
            Sell any of these gift cards.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Tap any brand for full pricing, country-specific rates, and a quick sell flow.
          </p>
        </div>

        {Object.keys(grouped).sort().map((cat) => (
          <div key={cat} className="mb-12">
            <h2 className="font-display text-xl font-bold text-[#0B3C5D] tracking-tight mb-4">{cat}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {grouped[cat].map((b, i) => (
                <motion.div
                  key={b.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: Math.min(i * 0.02, 0.3) }}
                >
                  <Link
                    to={`/sell/${b.id}`}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3.5 hover:border-[#0B3C5D] hover:shadow-md transition"
                    data-testid={`hub-brand-${b.id}`}
                  >
                    <span className="font-medium text-sm text-[#0B3C5D]">{b.name}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
